import { useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import {
  DisplayFormattedDate,
  EmptyIndicator,
  MoreOptions,
  NoEmailAddress,
  Pagination,
  Search,
  SortBy,
  User
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  PathAccessUsersAdd,
  PathAccessUsersInvite,
  PathAccessUsersInviteSingle,
  PathAccessUsersRemove,
  PathAccessUsersRemoveAll
} from '.';

import type { EmailTemplateData } from 'shared-types';
import type { GetPathUsersRes } from 'shared-types/responses';

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
}

const PathAccessUsers = ({ emailTemplates, emailTemplatesDefault }: Props) => {
  const {
    messages,
    pageState: { path },
    serverConfig: { userUseName }
  } = useReduxState();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState(userUseName ? 'name' : 'username');

  const { data, loading, updateData } = useApiData<GetPathUsersRes>({
    url:
      `/admin/paths/${path.id}/users` +
      `?page=${page}` +
      `&sortBy=${sortBy}` +
      (search ? `&search=${search}` : '')
  });

  return (
    <div className="users">
      <Box
        headerContent={
          <div className="header">
            <PathAccessUsersAdd
              emailTemplates={emailTemplates}
              emailTemplatesDefault={emailTemplatesDefault}
              updateUsers={updateData}
              userIds={data?.listOfUserIds}
            />

            <PathAccessUsersInvite
              disabled={!data?.users?.length}
              emailTemplates={emailTemplates}
              emailTemplatesDefault={emailTemplatesDefault}
              updateUsers={updateData}
            />
          </div>
        }
        loading={loading}
        padding={0}
        subHeaderContent={
          <div className="subheader">
            <div className="search">
              <Search
                onSearch={({ search }) => {
                  setSearch(search);
                  setPage(1);
                }}
                placeholder={messages.usersSearchUsers}
              />
            </div>

            <div className="sort">
              <SortBy
                onChange={({ sortBy }) => {
                  setSortBy(sortBy);
                  setPage(1);
                }}
                options={[
                  userUseName
                    ? { text: messages.commonName, value: 'name' }
                    : { text: messages.commonUsername, value: 'username' },
                  { text: messages.commonAdded, value: 'added' },
                  {
                    text: messages.commonInviteEmail,
                    value: 'inviteEmailDate'
                  },
                  { text: messages.usersLastLogin, value: 'lastLogin' }
                ]}
                value={sortBy}
              />
            </div>

            <MoreOptions category="lighter">
              <PathAccessUsersRemoveAll updateUsers={updateData} />
            </MoreOptions>
          </div>
        }
        title={messages.commonUsers}
      >
        {data?.users?.length ? (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell>{messages.commonUser}</BoxTableCell>
              <BoxTableCell>{messages.commonAdded}</BoxTableCell>
              <BoxTableCell>{messages.commonInviteEmail}</BoxTableCell>
              <BoxTableCell>{messages.usersLastLogin}</BoxTableCell>
              <BoxTableCell></BoxTableCell>
            </BoxTableHeader>

            {data.users.map((user) => (
              <BoxTableRow key={user.id}>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <User enableProfileLink={true} user={user} />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  icon="check-circle"
                  label={messages.commonAdded}
                >
                  <DisplayFormattedDate date={user.addedDate} />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  emptyValue="-"
                  label={messages.commonInviteEmail}
                  {...(user.inviteEmailDate ? { icon: 'envelope' } : {})}
                >
                  {user.inviteEmailDate ? (
                    <DisplayFormattedDate date={user.inviteEmailDate} />
                  ) : user.email ? null : (
                    <NoEmailAddress />
                  )}
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  emptyValue="-"
                  icon="key"
                  iconStyle={{
                    fontSize: '1.1rem',
                    position: 'relative',
                    top: -1
                  }}
                  label={messages.usersLastLogin}
                >
                  {user.lastLogin ? (
                    <DisplayFormattedDate date={user.lastLogin} />
                  ) : null}
                </BoxTableCell>

                <BoxTableCell shrink={true}>
                  <MoreOptions>
                    {user.email ? (
                      <PathAccessUsersInviteSingle
                        emailTemplates={emailTemplates}
                        emailTemplatesDefault={emailTemplatesDefault}
                        targetUser={user}
                        updateUsers={updateData}
                      />
                    ) : null}
                    <PathAccessUsersRemove
                      updateUsers={updateData}
                      userId={user.id}
                    />
                  </MoreOptions>
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <EmptyIndicator icon="user" title={messages.commonNoUsers} />
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => setPage(page)}
      />

      <style jsx>{`
        .header {
          display: flex;
          gap: 10px;
        }
        .subheader {
          display: flex;
          gap: 10px;
          width: 100%;
        }
        .sort {
          flex-grow: 1;
          min-width: 0;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .search {
            min-width: 300px;
          }
        }
      `}</style>
    </div>
  );
};

export default PathAccessUsers;
